import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

/**
 * Structural equality for form values. Form state here is plain data — the
 * object a page builds from a fetched row and hands back on save — so this
 * only has to handle objects, arrays and primitives. Key order is ignored,
 * which `JSON.stringify` comparison would not do.
 */
export function formValuesEqual(a: unknown, b: unknown): boolean {
  if (Object.is(a, b)) return true
  if (typeof a !== 'object' || typeof b !== 'object' || a === null || b === null) return false

  if (Array.isArray(a) || Array.isArray(b)) {
    if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) return false
    return a.every((item, i) => formValuesEqual(item, b[i]))
  }

  if (a instanceof Date || b instanceof Date) {
    return a instanceof Date && b instanceof Date && a.getTime() === b.getTime()
  }

  const aKeys = Object.keys(a as Record<string, unknown>)
  const bKeys = Object.keys(b as Record<string, unknown>)
  if (aKeys.length !== bKeys.length) return false
  return aKeys.every(
    key =>
      Object.prototype.hasOwnProperty.call(b, key) &&
      formValuesEqual((a as Record<string, unknown>)[key], (b as Record<string, unknown>)[key])
  )
}

export interface UseDirtyFormOptions {
  /**
   * Warn before the tab is closed or reloaded while edits are pending.
   * Defaults to true. This covers leaving the page entirely; it cannot cover
   * in-app navigation, which belongs to the router rather than this hook.
   */
  warnOnUnload?: boolean
}

export interface UseDirtyForm<T> {
  /** Current, possibly-edited values. */
  values: T
  /** Replace all values (accepts an updater, like `setState`). */
  setValues: (next: T | ((prev: T) => T)) => void
  /** Replace one field. */
  setField: <K extends keyof T>(field: K, value: T[K]) => void
  /**
   * Record a field that has already been stored on its own, outside the Save
   * button — a profile photo or a logo, which upload immediately. Writing it to
   * the baseline as well as the values keeps it out of the dirty comparison, so
   * an instant-saved image doesn't leave the save bar showing, and Discard
   * doesn't put the old one back. Pending edits to other fields survive.
   */
  commitField: <K extends keyof T>(field: K, value: T[K]) => void
  /** True when `values` differs from the baseline. */
  isDirty: boolean
  /** The last state known to be stored — what Discard returns to. */
  baseline: T
  /** Discard edits and return to the baseline. */
  reset: () => void
  /**
   * Adopt `next` as both the stored baseline and the current values — the
   * "here is the server's state" operation. Call it when a load or a reload
   * resolves.
   */
  setBaseline: (next: T) => void
  /**
   * Record that `next` is now stored, without touching the current values —
   * the "we just persisted exactly this" operation. Use it after a save whose
   * controls stay editable while the request is in flight: `setBaseline` would
   * overwrite anything typed during those few hundred milliseconds, silently
   * throwing the edit away. Keeping the values means the form correctly stays
   * dirty when, and only when, the user did type something.
   */
  markSaved: (next: T) => void
  /**
   * Adopt `stored` as the baseline while keeping the operator's pending
   * edits — the "the server holds this now, and the save did not fully land"
   * operation, for after a failed multi-request save. A field the operator
   * changed (its value differs from the old baseline) keeps that value and
   * stays dirty for a retry; every other field follows `stored`, so a retry
   * cannot send back a value someone else changed meanwhile, and Discard
   * returns to what the server holds.
   */
  reconcile: (stored: T) => void
}

/**
 * Owns the baseline / dirty / discard cycle for an explicit-Save settings form.
 *
 * Pages used to hand-roll this as a `hasChanges` boolean flipped inside every
 * onChange, which drifts: a field added later forgets to set the flag, and
 * discarding has to remember what the server last returned. Comparing against
 * a baseline snapshot makes both fall out for free — editing a field back to
 * its original value correctly reports clean.
 *
 * Pair it with `SettingsSaveBar`:
 *
 *   const form = useDirtyForm(EMPTY_SETTINGS)
 *   useEffect(() => { load().then(form.setBaseline) }, [])
 *   ...
 *   <SettingsSaveBar visible={form.isDirty} onSave={save} onDiscard={form.reset} />
 *
 * Treat the values as immutable. `reset` and `setBaseline` point the values and
 * the baseline at the same object, so mutating a nested array or object in place
 * (`values.items.push(...)`) would change the baseline too and the form would
 * never report itself dirty. Always replace: `setField('items', [...items, x])`.
 *
 * NORMALISE INTO THE VALUES, NOT ONLY INTO THE REQUEST
 *
 * The single most repeated defect of the rollout that produced this hook — ten
 * surfaces — and it wears a different disguise every time: a `.trim()`, a
 * `logoUrl === '' ? null`, a `visibleToEndUsers ? required : false`. The shape
 * is always the same. A save normalises a field on its way out, the form keeps
 * displaying the un-normalised text, and the baseline is set from the request
 * body — so the form is permanently dirty against its own contents and the
 * save bar can never be dismissed.
 *
 * So: apply the normalisation to the values BEFORE the request, and commit that
 * identical object to the baseline. The question to ask is not "what went over
 * the wire" but "what will the form display once this save lands".
 *
 * The exception is a normalisation the operator would see as an edit they did
 * not make. `EditIntentCategory` deliberately keeps its blank example rows out
 * of it: collapsing them would silently delete rows the operator was still
 * filling in.
 *
 * MARK SAVED ON EVERY SUCCESS PATH, INCLUDING CREATE
 *
 * A page that creates a record and then navigates to its own editor flips out
 * of create mode with the baseline still empty, so the bar comes up announcing
 * unsaved changes on something that was just saved. Call `markSaved` after the
 * create and update branches both, and before the `catch`, so a failure still
 * leaves the form dirty.
 *
 * Never mark saved optimistically. With a fire-and-forget `mutation.mutate(…)`
 * a failed save reports clean and takes the bar — the only retry affordance the
 * operator has — away with it. Re-baseline from the mutation's `onSuccess`.
 */
export function useDirtyForm<T extends object>(
  initial: T,
  options: UseDirtyFormOptions = {}
): UseDirtyForm<T> {
  const { warnOnUnload = true } = options

  const [baseline, setBaselineState] = useState<T>(initial)
  const [values, setValuesState] = useState<T>(initial)

  const isDirty = useMemo(() => !formValuesEqual(values, baseline), [values, baseline])

  // The listener is registered once and reads the flag through a ref, so
  // toggling dirty doesn't add and remove a listener on every keystroke.
  const isDirtyRef = useRef(isDirty)
  isDirtyRef.current = isDirty
  // `reconcile` reads the baseline through a ref too, so a `markSaved` or
  // `commitField` issued in the same tick counts.
  const baselineRef = useRef(baseline)
  baselineRef.current = baseline

  useEffect(() => {
    if (!warnOnUnload) return
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      if (!isDirtyRef.current) return
      // Browsers show their own wording; assigning returnValue is what opts in.
      e.preventDefault()
      e.returnValue = ''
    }
    window.addEventListener('beforeunload', onBeforeUnload)
    return () => window.removeEventListener('beforeunload', onBeforeUnload)
  }, [warnOnUnload])

  const setValues = useCallback((next: T | ((prev: T) => T)) => {
    setValuesState(prev => (typeof next === 'function' ? (next as (p: T) => T)(prev) : next))
  }, [])

  const setField = useCallback(<K extends keyof T>(field: K, value: T[K]) => {
    setValuesState(prev => ({ ...prev, [field]: value }))
  }, [])

  const commitField = useCallback(<K extends keyof T>(field: K, value: T[K]) => {
    setValuesState(prev => ({ ...prev, [field]: value }))
    setBaselineState(prev => ({ ...prev, [field]: value }))
    baselineRef.current = { ...baselineRef.current, [field]: value }
  }, [])

  const reset = useCallback(() => {
    setValuesState(baseline)
  }, [baseline])

  const setBaseline = useCallback((next: T) => {
    setBaselineState(next)
    setValuesState(next)
    baselineRef.current = next
  }, [])

  const markSaved = useCallback((next: T) => {
    setBaselineState(next)
    baselineRef.current = next
  }, [])

  const reconcile = useCallback((stored: T) => {
    const before = baselineRef.current
    setValuesState(prev => {
      const merged = { ...stored }
      for (const k of Object.keys(prev) as (keyof T)[]) {
        if (!formValuesEqual(prev[k], before[k])) merged[k] = prev[k]
      }
      return merged
    })
    setBaselineState(stored)
    baselineRef.current = stored
  }, [])

  return { values, setValues, setField, commitField, isDirty, baseline, reset, setBaseline, markSaved, reconcile }
}

export default useDirtyForm
